import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "./Motion";
import { SERVICES } from "@/lib/services";

const Card = ({ s, tall }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <Link
      ref={ref}
      to={`/services/${s.slug}`}
      data-testid={`service-card-${s.slug}`}
      className={`group relative block overflow-hidden bg-ink text-paper ${tall ? "h-[480px] lg:h-full" : "h-[420px] lg:h-auto lg:min-h-[340px]"}`}
    >
      <motion.img src={s.image} alt={s.title} style={{ y, scale: 1.2 }} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity duration-700 group-hover:opacity-100" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/50" />
      <div className="absolute inset-0 flex flex-col justify-between p-7 sm:p-9">
        <div className="flex items-start justify-between">
          <span className="eyebrow text-gold">{s.num} — {s.label}</span>
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-paper/40 transition-[background-color,border-color,transform] duration-500 group-hover:rotate-45 group-hover:border-gold group-hover:bg-gold group-hover:text-ink">
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </div>
        <div>
          <h3 className={`font-display leading-[1] ${tall ? "text-4xl sm:text-6xl" : "text-3xl sm:text-4xl"}`}>
            {s.lines[0]}<br /><em className="text-gold">{s.lines[1]}</em>
          </h3>
          <p className="mt-4 max-w-md text-sm sm:text-base text-paper/85">{s.short}</p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {s.items.slice(0, 3).map((it) => (
              <li key={it} className="border border-paper/25 px-3 py-1 text-xs text-paper/90 backdrop-blur-sm">{it}</li>
            ))}
          </ul>
        </div>
      </div>
    </Link>
  );
};

export const ServicesSummary = () => (
  <section id="services" data-testid="services-section" className="bg-paper-deep px-5 py-24 sm:px-8 lg:py-32">
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow text-gold-deep mb-6">Nos services</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            <MaskLines lines={["Trois savoir-faire,", <em key="e">une seule exigence.</em>]} />
          </h2>
        </div>
        <Reveal className="max-w-sm text-slate-600">
          Choisissez votre besoin : chaque prestation dispose de sa page dédiée avec le détail de nos interventions.
        </Reveal>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:grid-rows-2 lg:h-[760px]">
        <Reveal className="lg:col-span-7 lg:row-span-2 h-full"><Card s={SERVICES[0]} tall /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 h-full [&>a]:h-full"><Card s={SERVICES[1]} /></Reveal>
        <Reveal delay={0.2} className="lg:col-span-5 h-full [&>a]:h-full"><Card s={SERVICES[2]} /></Reveal>
      </div>
    </div>
  </section>
);
